import React, { useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CustomAvatar from 'components/ui/customAvatar';
import { MissingPhoto } from 'Interfaces/IGlobalTypeConfig';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  GroupedProductForCard,
  OwnerForCard,
} from '../Reducers/OrderingProductReducer';

type AggregatedCardProps = {
  t: (k: string) => string;
  group: GroupedProductForCard;

  // (productId, unitPrice, vendorProductUrl, owners)
  handleUpdateProductFields: (
    productId: string,
    unitPrice: number,
    vendorProductUrl: string,
    owners: OwnerForCard[]
  ) => void;

  onRemoveGroup: (urlKey: string) => void;
  onRemoveOwner: (productId: string, ownerId: string) => void;
  onChangeVendorUrl: (
    oldUrl: string,
    nextUrl: string,
    owners: OwnerForCard[]
  ) => void;
  onChangeOwnerOrdered: (
    productId: string,
    next: number,
    owners: OwnerForCard[],
    ownerId: string
  ) => void;
};

export const AggregatedOrderProductCard: React.FC<AggregatedCardProps> = ({
  t,
  group,
  handleUpdateProductFields,
  onRemoveGroup,
  onRemoveOwner,
  onChangeVendorUrl,
  onChangeOwnerOrdered,
}) => {
  // headerProductId: prøv ekte productId fra første owner; fallback til group.id
  const headerProductId = group.owners?.[0]?.productId || group.id || '';

  // Hvis ingen owners: lag én syntetisk linje, MEN prøv bruke headerProductId som er ekte
  const displayOwners: OwnerForCard[] = useMemo(() => {
    if ((group.owners ?? []).length > 0) return group.owners;

    const name = t('order.orderPost.noOwner') || 'Ingen eier';
    return [
      {
        productId: headerProductId,
        owner: {
          id: '', // tom streng = wildcard
          name,
          email: '',
          phone: '',
          url: '',
        } as any,
        ticketId: '', // viktig når du vil matche uten owner
        ticketNo: '',
        companyName: group.vendor?.name ?? '',
        companyUrl: group.vendor?.url ?? '',
        quantity: group.quantity ?? 0,
        orderedQuantity: group.orderedQuantity ?? 0,
        alreadyOrdered: group.alreadyOrdered ?? 0,
        toOrder: (group.quantity ?? 0) - (group.alreadyOrdered ?? 0),
      } as OwnerForCard,
    ];
  }, [
    group.owners,
    group.vendor?.name,
    group.vendor?.url,
    group.quantity,
    group.orderedQuantity,
    group.alreadyOrdered,
    headerProductId,
    t,
  ]);

  // --- Lokale states (synkes mot props) ---
  const [urlEditable, setUrlEditable] = React.useState(
    group.vendorProductUrl || ''
  );
  const [priceEditable, setPriceEditable] = React.useState<string>(
    Number.isFinite(group.unitPrice as any) ? String(group.unitPrice) : ''
  );

  // antall per ownerId (kan være '')
  const [qtyByOwner, setQtyByOwner] = React.useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        displayOwners.map((o) => [
          o.owner?.id ?? '',
          String(o.orderedQuantity ?? 0),
        ])
      )
  );

  const [totalOrdered, setTotalOrdered] = React.useState<number>(
    group.orderedQuantity || 0
  );
  const alreadyOrdered = group.alreadyOrdered || 0;
  const totalNeed = (group.quantity || 0) - alreadyOrdered;

  // --- Sync fra props ---
  useEffect(() => {
    setUrlEditable(group.vendorProductUrl || '');
  }, [group.vendorProductUrl]);

  useEffect(() => {
    const next = Number.isFinite(group.unitPrice as any)
      ? String(group.unitPrice)
      : '';
    setPriceEditable(next);
  }, [group.unitPrice]);

  useEffect(() => {
    setQtyByOwner(
      Object.fromEntries(
        displayOwners.map((o) => [
          o.owner?.id ?? '',
          String(o.orderedQuantity ?? 0),
        ])
      )
    );
    setTotalOrdered(group.orderedQuantity || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayOwners]);

  const ok = totalOrdered === totalNeed;

  // --- Commits/handlers ---
  const commitUrlChange = () => {
    const oldUrl = (group.vendorProductUrl || '').trim();
    const nextUrl = (urlEditable || '').trim();
    if (!nextUrl || nextUrl === oldUrl) return;
    onChangeVendorUrl(oldUrl, nextUrl, displayOwners);
  };

  const commitPriceChange = () => {
    const n = Number((priceEditable ?? '').toString().replace(',', '.'));
    if (!Number.isFinite(n) || !headerProductId) return;
    handleUpdateProductFields(
      headerProductId,
      Number(n.toFixed(2)),
      group.vendorProductUrl || '',
      displayOwners
    );
  };

  const commitQuantityChange = (productId: string, ownerId: string) => {
    const raw = qtyByOwner[ownerId] ?? '';
    const n = Number(raw);
    if (!Number.isFinite(n)) return;

    if (n <= 0) {
      onRemoveOwner(productId, ownerId); // tom ownerId tolkes som “sett til 0 / fjern”
      return;
    }
    onChangeOwnerOrdered(productId, n, displayOwners, ownerId);
  };

  return (
    <Accordion
      disableGutters
      sx={{
        mb: 1.5,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'primary.main',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              fontSize: 44,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          />
        }
        sx={{ px: 2, py: 1.5, position: 'relative' }}
      >
        <Stack
          direction='row'
          spacing={2}
          sx={{ alignItems: 'center', width: '100%' }}
        >
          {/* Bilde */}
          {group.imageUrl ? (
            <Box
              component='img'
              src={group.imageUrl}
              alt='prod'
              sx={{
                width: 96,
                height: 96,
                borderRadius: 2,
                objectFit: 'contain',
                bgcolor: 'primary.light',
                flexShrink: 0,
              }}
            />
          ) : (
            <CustomAvatar
              avatarName={group.name || ''}
              imgWidth={96}
              imgHeight={96}
            />
          )}

          {/* Midten */}
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={group.name}
            >
              {group.name}
            </Typography>

            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ minWidth: 0, mt: 0.25 }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={group.vendor?.name}
              >
                {group.vendor?.name || ''}
              </Typography>
              {group.vendor?.url ? (
                <Box
                  component='img'
                  src={group.vendor.url || MissingPhoto.COMPANY_URL}
                  alt='vendor'
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    objectFit: 'contain',
                    bgcolor: 'primary.light',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <CustomAvatar
                  avatarName={group.vendor?.name || ''}
                  imgWidth={48}
                  imgHeight={48}
                />
              )}
            </Stack>

            {/* URL – redigerbar */}
            <TextField
              size='small'
              value={urlEditable}
              onFocus={(e) => (e.target as HTMLInputElement).select()}
              onChange={(e) => setUrlEditable(e.target.value)}
              onBlur={commitUrlChange}
              fullWidth
              sx={{
                mt: 0.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'primary.light',
                },
              }}
              InputProps={{
                endAdornment: (
                  <Tooltip title={urlEditable || ''}>
                    <a
                      href={urlEditable || '#'}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={(e) => {
                        if (!urlEditable) e.preventDefault();
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        textDecoration: 'underline',
                      }}
                    >
                      <OpenInNewIcon fontSize='small' />
                    </a>
                  </Tooltip>
                ),
              }}
            />
          </Stack>

          {/* Høyre: ordered/need + kostpris */}
          <Stack sx={{ width: 280, flexShrink: 0 }}>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ justifyContent: 'flex-end' }}
            >
              <Chip
                size='small'
                variant='filled'
                color={ok ? 'success' : 'warning'}
                label={`${t(
                  'order.orderPost.orderedLabel'
                )}: ${totalOrdered} / ${totalNeed}`}
                sx={{ fontWeight: 600 }}
              />
            </Stack>

            <TextField
              label={
                <Typography sx={{ fontSize: 12 }}>
                  {t('bluu.costPrice')}
                </Typography>
              }
              type='number'
              size='small'
              value={priceEditable}
              onFocus={(e) => (e.target as HTMLInputElement).select()}
              onChange={(e) => {
                setPriceEditable(e.target.value);
                if (!headerProductId) return;
                handleUpdateProductFields(
                  headerProductId,
                  Number(e.target.value),
                  group.vendorProductUrl || '',
                  displayOwners
                );
              }}
              onBlur={commitPriceChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter')
                  (e.currentTarget as HTMLInputElement).blur();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ color: 'text.secondary', mr: 0.5 }}
                  >
                    {t('bluu.currency').toUpperCase()}
                  </InputAdornment>
                ),
                inputProps: { step: '0.01', min: 0, inputMode: 'decimal' },
              }}
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'primary.light',
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                  appearance: 'textfield',
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                  { WebkitAppearance: 'none', margin: 0 },
              }}
            />
          </Stack>

          {/* Fjern gruppe */}
          <IconButton
            aria-label='remove-group'
            onClick={(e) => {
              e.stopPropagation();
              onRemoveGroup(group.id);
            }}
            onFocus={(e) => e.stopPropagation()}
            size='small'
            sx={{ position: 'absolute', top: 2, right: 2, zIndex: 1 }}
          >
            <DeleteOutlineIcon
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'error.main' },
              }}
            />
          </IconButton>
        </Stack>
      </AccordionSummary>

      {/* Detaljer */}
      <AccordionDetails sx={{ px: 2, pb: 2 }}>
        <Stack spacing={1.25}>
          {displayOwners.map((o) => {
            const ownerId = o.owner?.id ?? '';
            const ownerName =
              o.owner?.name ?? (t('order.orderPost.noOwner') || 'Ingen eier');

            return (
              <Stack
                key={`${o.productId}-${ownerId || 'no-owner'}`}
                direction='row'
                spacing={1.5}
                alignItems='center'
                sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 2 }}
              >
                {/* Avatar */}
                {o.owner?.url ? (
                  <Box
                    component='img'
                    src={o.owner?.url || MissingPhoto.EMPLOYEE_URL}
                    alt='owner'
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      objectFit: 'contain',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <CustomAvatar
                    avatarName={ownerName}
                    imgWidth={64}
                    imgHeight={64}
                  />
                )}

                {/* Navn + selskap */}
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {ownerName}
                  </Typography>

                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ minWidth: 0, mt: 0.25 }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        opacity: 0.9,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={o.companyName}
                    >
                      {o.companyName || ''}
                    </Typography>
                    {o.companyUrl ? (
                      <Box
                        component='img'
                        src={o.companyUrl || MissingPhoto.COMPANY_URL}
                        alt='company'
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          objectFit: 'contain',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <CustomAvatar
                        avatarName={o.companyName || ''}
                        imgWidth={40}
                        imgHeight={40}
                      />
                    )}
                  </Stack>
                </Stack>

                {/* Antall */}
                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='center'
                  sx={{ width: 260, justifyContent: 'flex-end' }}
                >
                  <TextField
                    size='small'
                    type='number'
                    value={qtyByOwner[ownerId] ?? ''}
                    onFocus={(e) => (e.target as HTMLInputElement).select()}
                    onChange={(e) => {
                      const v = e.target.value;
                      setQtyByOwner((prev) => ({ ...prev, [ownerId]: v }));
                      onChangeOwnerOrdered(
                        o.productId || headerProductId,
                        Number(v),
                        displayOwners,
                        ownerId
                      );
                    }}
                    onBlur={() =>
                      commitQuantityChange(
                        o.productId || headerProductId,
                        ownerId
                      )
                    }
                    InputProps={{
                      inputProps: { min: 0, max: o.quantity ?? 0 },
                    }}
                    sx={{
                      width: 120,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: 14, opacity: 0.9 }}>
                    {Math.ceil((o.quantity ?? 0) - (o.alreadyOrdered ?? 0))}
                  </Typography>

                  <Tooltip
                    title={
                      (o.alreadyOrdered ?? 0) > 0
                        ? t('order.orderPost.cannotRemoveAlreadyOrdered')
                        : t('order.orderPost.removeFromOrder')
                    }
                  >
                    <span>
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          if ((o.alreadyOrdered ?? 0) > 0) return;
                          if (
                            window.confirm(
                              t('order.orderPost.confirmRemoveOwnerItem')
                            )
                          ) {
                            onRemoveOwner(
                              o.productId || headerProductId,
                              ownerId
                            );
                          }
                        }}
                        disabled={(o.alreadyOrdered ?? 0) > 0}
                        sx={{ ml: 0.5 }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color:
                              (o.alreadyOrdered ?? 0) > 0
                                ? 'text.disabled'
                                : 'error.main',
                          }}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
